/**
 * Community Audit Policy Breakdown Component
 *
 * Displays per-policy correctness metrics from the most recent report's byPolicy array.
 * Shows policies sorted by lowest correctness first (highlighting problem areas),
 * with triggered/non-triggered summary counts from comprehensiveAnalytics.policyAnalysis.
 *
 * This component replaces CommunityAuditLabelAnalysis when the user toggles to "Policies" view.
 */

import { ProgressBar } from '@core/components';
import type { ComprehensiveAnalytics } from '@core/graphql/types/schema';
import * as shimmerStyles from '@internal-audit/components/shared/shimmer.css';
import type { AuditReport } from '@internal-audit/domain';
import { Shimmer, Text } from '@soteria/design-system-ve';
import { useEffect, useMemo, useState } from 'react';

import * as styles from './CommunityAuditPolicyBreakdown.css';

interface CommunityAuditPolicyBreakdownProps {
  auditReports: AuditReport[];
  comprehensiveAnalytics: ComprehensiveAnalytics | null;
  isLoading?: boolean;
}

/** Maximum number of policies to show in the panel */
const MAX_POLICIES_SHOWN = 4;

export function CommunityAuditPolicyBreakdown({ auditReports, comprehensiveAnalytics, isLoading = false }: CommunityAuditPolicyBreakdownProps) {
  const [animate, setAnimate] = useState(false);

  // Respect prefers-reduced-motion for accessibility (SSR-safe guard)
  const prefersReducedMotion = useMemo(() => (typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : true), []);

  // Get per-policy data from the most recent report
  const topPolicies = useMemo(() => {
    if (!auditReports.length) return [];

    // Find the most recent report with byPolicy data
    const sorted = [...auditReports].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    const reportWithPolicies = sorted.find(r => r.metrics?.byPolicy?.length);

    if (!reportWithPolicies?.metrics?.byPolicy) return [];

    // Sort by lowest correctness first (highlight problem areas)
    return [...reportWithPolicies.metrics.byPolicy]
      .sort((a, b) => a.overallCorrectnessPercentage - b.overallCorrectnessPercentage)
      .slice(0, MAX_POLICIES_SHOWN);
  }, [auditReports]);

  // Trigger animation when data loads (skip if user prefers reduced motion)
  useEffect(() => {
    if (!isLoading && (topPolicies.length > 0 || comprehensiveAnalytics)) {
      if (prefersReducedMotion) {
        setAnimate(true);
      } else {
        const timer = setTimeout(() => setAnimate(true), 200);

        return () => clearTimeout(timer);
      }
    } else {
      setAnimate(false);
    }
  }, [isLoading, topPolicies, comprehensiveAnalytics, prefersReducedMotion]);

  // Render loading state
  if (isLoading) {
    return (
      <div className={styles.panel}>
        <Text className={styles.title} size="base" weight="medium">
          <Shimmer className={shimmerStyles.shimmerHeader} />
        </Text>
        <div className={styles.content}>
          {['p1', 'p2', 'p3', 'p4'].map(key => (
            <div key={`shimmer-${key}`} className={styles.policyItem}>
              <div className={styles.policyHeader}>
                <Shimmer className={shimmerStyles.shimmerLabel} />
                <Shimmer className={shimmerStyles.shimmerSmallValue} />
              </div>
              <Shimmer className={shimmerStyles.shimmerProgressBar} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // No data state
  if (topPolicies.length === 0 && !comprehensiveAnalytics?.policyAnalysis) {
    return (
      <div className={styles.panel}>
        <Text className={styles.title} size="base" weight="medium">
          Policy Correctness
        </Text>
        <div className={styles.content}>
          {[1, 2, 3, 4].map(i => (
            <div key={`empty-${i}`} className={styles.policyItem}>
              <div className={styles.policyHeader}>
                <Text className={styles.policyLabel} color="secondary" size="sm">
                  —
                </Text>
                <Text className={styles.policyValue} size="sm" weight="semibold">
                  N/A
                </Text>
              </div>
              <ProgressBar animated={false} aria-label="No data" color="secondary" size="panel" value={0} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const policyAnalysis = comprehensiveAnalytics?.policyAnalysis;
  const shouldAnimate = animate && !prefersReducedMotion;

  return (
    <div className={styles.panel}>
      <Text className={styles.title} size="base" weight="medium">
        Policy Correctness
      </Text>
      <div className={styles.content}>
        {/* Per-policy correctness bars */}
        {topPolicies.map(policy => {
          const pct = Math.round(policy.overallCorrectnessPercentage);

          return (
            <div key={policy.policyId} className={styles.policyItem}>
              <div className={styles.policyHeader}>
                <Text className={styles.policyLabel} color="secondary" size="sm">
                  {policy.policyName}
                </Text>
                <Text className={styles.policyValue} size="sm" weight="semibold">
                  {pct}%
                </Text>
              </div>
              <ProgressBar
                animated={shouldAnimate}
                aria-label={`${policy.policyName}: ${pct}%`}
                color={pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error'}
                size="panel"
                value={animate ? pct : 0}
              />
            </div>
          );
        })}

        {/* Summary counts from policyAnalysis */}
        {policyAnalysis && (
          <div className={styles.summarySection}>
            <div className={styles.summaryRow}>
              <Text className={styles.summaryLabel} color="secondary" size="xs">
                Triggered correct
              </Text>
              <Text className={styles.summaryValue} size="xs" weight="semibold">
                {policyAnalysis.totalTriggeredAssessments.percentage}%
              </Text>
            </div>
            <div className={styles.summaryRow}>
              <Text className={styles.summaryLabel} color="secondary" size="xs">
                Non-triggered correct
              </Text>
              <Text className={styles.summaryValue} size="xs" weight="semibold">
                {policyAnalysis.totalNonTriggeredAssessments.percentage}%
              </Text>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
